import {useContext, useEffect} from "react"
import type {
    CropParams,
    CropState,
    Dimensions,
    EditorMode,
    ImageState,
    Position,
} from "../types/types"
import {CropperContext} from "../context/CropperContext"
import {CONSTANTS} from "../constants/constants"

export const useCropper = (materialIndex?: number) => {
    const context = useContext(CropperContext)

    if (!context) {
        throw new Error("useCropper must be used within CropperProvider")
    }

    const {state, dispatch} = context
    useEffect(() => {
        if (materialIndex !== undefined && !state.materials[materialIndex]) {
            dispatch({
                type: "INIT_MATERIAL",
                materialIndex,
                cropDimensions: {
                    width: CONSTANTS.DEFAULT_CROP_WIDTH,
                    height: CONSTANTS.DEFAULT_CROP_HEIGHT,
                },
            })
        }
    }, [materialIndex, dispatch, state.materials])

    const material =
        materialIndex !== undefined ? state.materials[materialIndex] : null

    if (!material) {
        return null
    }

    const {
        imageState,
        cropState,
        mode,
        cropParams,
        isProcessing,
        downloadUrl,
        uploadProgress,
        currentImageUrl,
        videoElement,
        renderData,
    } = material

    const setImageState = (imageState: Partial<ImageState>) => {
        if (materialIndex === undefined) return
        dispatch({
            type: "SET_IMAGE_STATE",
            materialIndex,
            imageState,
        })
    }

    const setCropState = (cropState: Partial<CropState>) => {
        if (materialIndex === undefined) return
        dispatch({
            type: "SET_CROP_STATE",
            materialIndex,
            cropState,
        })
    }

    const setMode = (mode: EditorMode) => {
        if (materialIndex === undefined) return
        dispatch({
            type: "SET_MODE",
            materialIndex,
            mode,
        })
    }

    const setCropParams = (params: CropParams) => {
        if (materialIndex === undefined) return
        dispatch({
            type: "SET_CROP_PARAMS",
            materialIndex,
            params,
        })
    }

    const setIsProcessing = (isProcessing: boolean) => {
        if (materialIndex === undefined) return
        dispatch({
            type: "SET_PROCESSING",
            materialIndex,
            isProcessing,
        })
    }

    const setDownloadUrl = (downloadUrl: string | null) => {
        if (materialIndex === undefined) return
        dispatch({
            type: "SET_DOWNLOAD_URL",
            materialIndex,
            downloadUrl,
        })
    }

    const setUploadProgress = (uploadProgress: number) => {
        if (materialIndex === undefined) return
        dispatch({
            type: "SET_UPLOAD_PROGRESS",
            materialIndex,
            uploadProgress,
        })
    }

    const setCropDimensions = (dimensions: Dimensions) => {
        if (materialIndex === undefined) return
        dispatch({
            type: "SET_CROP_DIMENSIONS",
            materialIndex,
            dimensions,
        })
    }

    const setCurrentImageUrl = (currentImageUrl: string | null) => {
        if (materialIndex === undefined) return
        dispatch({
            type: "SET_CURRENT_IMAGE_URL",
            materialIndex,
            currentImageUrl,
        })
    }

    const setVideoElement = (videoElement: HTMLVideoElement | null) => {
        if (materialIndex === undefined) return
        dispatch({
            type: "SET_VIDEO_ELEMENT",
            materialIndex,
            videoElement,
        })
    }

    const setRenderData = (
        renderData: {position: Position; dimensions: Dimensions} | null
    ) => {
        if (materialIndex === undefined) return
        dispatch({
            type: "SET_RENDER_DATA",
            materialIndex,
            renderData,
        })
    }

    const resetMaterial = () => {
        if (materialIndex === undefined) return
        dispatch({
            type: "RESET_MATERIAL",
            materialIndex,
        })
    }

    return {
        imageState,
        cropState,
        mode,
        cropParams,
        isProcessing,
        downloadUrl,
        uploadProgress,
        currentImageUrl,
        videoElement,
        renderData,

        setImageState,
        setCropState,
        setMode,
        setCropParams,
        setIsProcessing,
        setDownloadUrl,
        setUploadProgress,
        setCropDimensions,
        setCurrentImageUrl,
        setVideoElement,
        setRenderData,
        resetMaterial,
    }
}
